import Responsive from 'app/components/responsive';
import { Select, type SelectProps } from 'app/design/primitives/select';
import { getI18nLanguage } from 'app/lib/language';
import { getCountryProvincesByLanguage } from 'app/localization/provinces';
import { useTranslation } from 'react-i18next';
import { SelectPickerHtml } from './select-html';

interface AdministrativeAreaSelectProps extends Omit<SelectProps<string>, 'options'> {
  country: string;
}

export function AdministrativeAreaSelect({ country, disabled, ...rest }: AdministrativeAreaSelectProps) {
  const { t } = useTranslation();
  const i18nLanguage = getI18nLanguage();

  const provinces = getCountryProvincesByLanguage(country, i18nLanguage);

  const selectedProvince = (() => {
    if (!rest.value || !provinces.length) return undefined;
    return provinces.find((province) => province.code === rest.value);
  })();

  return (
    <Responsive
      {...rest}
      desktop={
        <SelectPickerHtml
          data={provinces.map((province) => ({
            key: province.code,
            value: province.name,
          }))}
          setSelected={(value: string) => {
            rest.onChange(value);
          }}
          defaultOption={
            selectedProvince
              ? {
                  key: selectedProvince.code,
                  value: selectedProvince.name,
                }
              : undefined
          }
          placeholder={t('administrativeAreaFieldLabel', { ns: 'my-address' })}
          disabled={disabled || !provinces.length}
        />
      }
      mobile={
        <Select
          {...rest}
          allowSearch={false}
          disabled={disabled || !provinces.length}
          options={[
            {
              label: t('administrativeAreaFieldLabel', { ns: 'my-address' }),
              values: provinces.map((province) => ({
                label: province.name,
                value: province.code,
              })),
            },
          ]}
        />
      }
    />
  );
}
